import {
  chooseRivalInsight,
  type RivalInsight,
} from "../../../shared/presentation/rival-insight.js";
import { relationLabel } from "../rivalry/comparison-selection.js";
import { RivalryCalendarControls } from "../rivalry/rivalry-calendar-controls.js";
import { ComparisonLoadState } from "../rivalry/comparison-detail-page.js";
import { comparisonAnalyticsLabel } from "../../../shared/presentation/rivalry-calendar.js";
import type { ComparisonDetail } from "../../../shared/contracts/product-pages.js";
import type { RoundSnapshot } from "../../../domain/round/round-types.js";
import { selectOrderedHoles } from "../../../domain/round/round-selectors.js";
import { Button } from "@/components/ui/button";
import {
  ApprovedAvatar,
  ApprovedHeading,
} from "../../shell/approved-primitives.js";
import {
  ArrowRight,
  Shield,
  Sparkles,
  Swords,
  UserPlus,
  Flag,
} from "../../shell/approved-icons.js";
import {
  clubhouseAvatarId,
  viewerRecord,
  type ClubhousePerson,
} from "./clubhouse-relations-model.js";

export function ClubhouseNextRound({
  round,
  followed,
  onOpen,
}: {
  round: RoundSnapshot | null;
  followed: boolean;
  onOpen(): void;
}): React.JSX.Element {
  return (
    <section className="fr-home-next">
      <div className="fr-home-next-caption">
        <span>{round ? "YOUR ROUND IS IN PLAY" : "YOUR NEXT ROUND"}</span>
      </div>
      <Button variant="gold-gradient" size="block" onClick={onOpen}>
        <Flag data-icon="inline-start" />
        {followed ? "Follow round" : round ? "Continue round" : "Start a round"}
        <ArrowRight data-icon="inline-end" />
      </Button>
      <p>
        {round
          ? `${round.course.name} · ${selectOrderedHoles(round).length} holes · ${round.participants.length} players`
          : "Choose your course. Bring your rivals."}
      </p>
    </section>
  );
}

export function ClubhouseRivals(props: {
  people: ClubhousePerson[];
  availableYears?: number[];
  hasSimulator?: boolean;
  loading?: boolean;
  error?: string | null;
  onRetry?(): void;
  onSelect(person: ClubhousePerson): void;
  onAll(): void;
  onAdd(): void;
}): React.JSX.Element {
  if (!props.people.length && !props.loading && !props.error)
    return (
      <section className="fr-home-invitation">
        <Swords size={29} />
        <h2>Who brings out your best?</h2>
        <p>
          Bring a rival to your next round. Your shared scorecards will start
          the story.
        </p>
        <Button variant="ghost" onClick={props.onAdd}>
          <UserPlus />
          Add your first rival
        </Button>
      </section>
    );
  return (
    <section className="fr-home-circle">
      <ApprovedHeading
        title="Your rivals"
        onTitleClick={props.onAll}
        action="View all"
        onAction={props.onAll}
      >
        <RivalryCalendarControls
          availableYears={props.availableYears ?? []}
          hasSimulator={props.hasSimulator ?? false}
        />
      </ApprovedHeading>
      <ComparisonLoadState
        loading={props.loading ?? false}
        error={props.error ?? null}
        onRetry={() => props.onRetry?.()}
      />
      {!props.loading && !props.error ? (
        <div
          className="fr-home-circle-list"
          data-count={Math.min(props.people.length, 3)}
        >
          {props.people.slice(0, 3).map((person) => (
            <button
              type="button"
              className="fr-home-rival"
              key={person.key}
              onClick={() => props.onSelect(person)}
            >
              <ApprovedAvatar
                name={person.name}
                participantId={clubhouseAvatarId(person)}
                size={44}
              />
              <strong>{person.name}</strong>
              <PersonRecord person={person} />
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function PersonRecord({
  person,
}: {
  person: ClubhousePerson;
}): React.JSX.Element {
  const relation = person.relation;
  if (!relation)
    return (
      <span>
        {person.rival?.rounds
          ? "No qualified comparison yet"
          : "First round ahead"}
      </span>
    );
  const record = viewerRecord(relation);
  return (
    <>
      <div className="fr-home-rival-record">
        <b>
          {record.own}
          <small>You</small>
        </b>
        <i>–</i>
        <b>
          {record.other}
          <small>Them</small>
        </b>
      </div>
      <span>
        {relation.record.roundCount === 0 && relation.testRounds?.length
          ? `${relation.testRounds.length} test rounds · record only`
          : `${relation.sharedRoundCount ?? relation.record.completedRoundCount} shared ${(relation.sharedRoundCount ?? relation.record.completedRoundCount) === 1 ? "round" : "rounds"}`}
        {(relation.sharedRoundCount ?? 0) > 0 &&
        relation.record.contestCount === 0
          ? " · No duels yet"
          : ""}
        {relation.record.ties ? ` · ${relation.record.ties} draws` : ""}
        {relation.record.challengeCount
          ? ` · ${relation.record.challengeCount} ${relation.record.challengeCount === 1 ? "challenge" : "challenges"}`
          : ""}
      </span>
    </>
  );
}

export function ClubhouseInsight({
  detail,
  onOpen,
  finding: selected,
}: {
  detail: ComparisonDetail | null;
  finding?: RivalInsight | null;
  onOpen(finding: RivalInsight | null): void;
}): React.JSX.Element {
  const finding =
    selected ??
    (detail ? (chooseRivalInsight([detail])?.finding ?? null) : null);
  return (
    <button
      type="button"
      className="fr-home-insight"
      onClick={() => onOpen(finding)}
    >
      <Sparkles size={27} />
      <span>
        <span className="fr-home-kicker">INSIGHTS</span>
        <strong>
          {finding?.text ?? "Your first shared result starts the story"}
        </strong>
        <span>
          {detail
            ? relationLabel(detail.relation)
            : "Opening holes. Front nine. Back nine. Every shared round adds context."}
        </span>
        <small>
          {finding && detail
            ? `${comparisonAnalyticsLabel(finding.filters)} · ${finding.basis}`
            : "Choose a rival to see the available evidence."}
        </small>
      </span>
      <ArrowRight size={17} />
    </button>
  );
}

export function ClubhouseSeason({
  title,
  summary,
  onOpen,
}: {
  title?: string | undefined;
  summary?: string | undefined;
  onOpen(): void;
}): React.JSX.Element {
  return (
    <section className="fr-home-season-section">
      <ApprovedHeading title="Your season" action="Open" onAction={onOpen} />
      <button type="button" className="fr-home-season" onClick={onOpen}>
        <Shield size={32} />
        <div>
          <strong>{title ?? "Season"}</strong>
          <small>
            {summary ?? "See your season and its agreed competition rules."}
          </small>
        </div>
        <ArrowRight size={17} />
      </button>
    </section>
  );
}
